import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import { inject, injectable } from "inversify";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import type { IOrganizationStore } from "../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../services/IOrganizationStore";
import { fromPromise, IPromiseBasedObservable } from "mobx-utils";
import type { SecurityIntegrations, IncidentTypeValue, FileScanningIntegrations } from "@whatslly/types/messages-server/destinations";
import {
  ListDestinationsResponse,
  ListDestinationsShallowResponse,
  MessageDestination,
  TestConnectionResponse,
  DESTINATION_INTEGRATION_TYPES,
  isOtherSecurityDestination,
} from "@whatslly/types/messages-server/destinations";
import { deleteJSON, getJSON, postJSON, putJSON } from "@whatslly/commons/apiUtils";
import type { Destination } from "@whatslly/types/messages-server/destinations";
import i18n from "../../i18n/config";
import { parseOrReturnError } from "@whatslly/commons/parseOrReturnError";
import { TFunction } from "i18next";
import { modalService } from "@whatslly/ui-components/modals";
import { ConfirmModal } from "@whatslly/ui-components/modals/ConfirmModal";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { SecurityOtherModal } from "./SecurityOtherModal";
import type { ConfigKey } from "@whatslly/types/OrgConfigs";
import { AuditLogNamespace } from "@whatslly/types/integrations/auditLog/auditLog";
import uniq from "lodash/uniq";
import cloneDeep from "lodash/cloneDeep";
import { OrgConfigsStore } from "../../services/OrgConfigsStore";
import { AMAZON_TRANSCRIBE_API_VALIDATION_ERROR, AMAZON_TRANSCRIBE_S3_VALIDATION_ERROR } from "@whatslly/commons/destinationValidationErrors";
import type { UserType } from "@whatslly/types/User";
import type { IconType } from "react-icons";
import { OrgConfigs } from "@whatslly/types/OrgConfigs";
import { PLATFORM_TYPES, PlatformType, platformTypeOptions } from "@whatslly/types/tuvis-messaging/TuvisMessagingPlatforms";
import { hasAnyPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { BsMicrosoftTeams } from "react-icons/bs";
import { FaDiscord, FaSlack } from "react-icons/fa6";
import { WhatsAppBusinessApiSettingsStore } from "../TuvisMessaging/settings/WhatsAppBusinessApiSettingsStore";
import { TwilioSettingsStore } from "../TuvisMessaging/settings/Twilio/TwilioSettingsStore";
import { SlackSettingsStore } from "../TuvisMessaging/platforms/slack/SlackSettingsStore";
import { TeamsSettingsStore } from "../TuvisMessaging/platforms/teams/TeamsSettingsStore";
import { ArchiveTeamsSettingsStore } from "../TuvisMessaging/platforms/archive-teams/ArchiveTeamsSettingsStore";
import { ArchiveDiscordSettingsStore } from "../TuvisMessaging/platforms/archive-discord/ArchiveDiscordSettingsStore";

export const NAMESPACE_ORG_PERMISSIONS: Record<AuditLogNamespace, ConfigKey> = {
  integrations: "allow_platform_integrations",
  sidecars: "sidecars_enabled",
  users: "allow_inviting_users",
  auth: "allow_inviting_users",
  userGroups: "allow_inviting_users",
  settings: "allow_platform_global_layout",
  dashboards: "show_dashboards_ui",
  waba: "allow_waba",
  twilio: "allow_twilio",
  dlp: "allow_dlp",
  surveillance: "enable_surveillance",
  incident: "allow_dlp",
  urlPolicies: "allow_url_policy",
  browserControls: "allow_tuvis_browser",
  email: "allow_inviting_users",
  tuvisContacts: "allow_tuvis_contacts",
  keywordInsights: "allow_keyword_insights",
  phonesWhitelist: "allow_phones_whitelist",
  phonesBlocklist: "allow_phones_blocklist",
  blockedPhones: "allow_blocked_phones",
  fraudDetection: "fraud_detection",
  customFilters: "show_custom_filters_ui",
  accounts: "allow_inviting_users",
  archive: "allow_archive",
  platforms: "allow_slack_platform",
  saml: "show_saml_settings",
  botFlows: "allow_bot_builder",
};

type ChannelsOverviewItemIntegrationType = "channels" | "tuvis-mobile" | "platforms" | "archive";

type ChannelsOverviewItemForDestinations = {
  name: string;
  category: string;
  permission: boolean;
  connected: boolean;
  navigateTo?: string;
  icon?: IconType;
  img?: string;
  imgType?: "svg" | "png";
  showRevokeButton?: boolean;
  revokeFunction?: () => void | Promise<void>;
  disabled?: boolean;
  disabledTooltip?: string;
  integrationType: ChannelsOverviewItemIntegrationType;
  isActive: (user?: UserType) => boolean;
};
export type DesktopChannel = "whatsapp" | "telegram" | "imessage";
export type CommonChannel = "waba" | "twilio";
type ChannelsStoreConfig = Pick<OrgConfigs, (typeof keys)[number]>;

const keys = [
  "hide_whatsapp_channel",
  "hide_telegram_channel",
  "allow_imessage",
  "allow_telegram",
  ...platformTypeOptions.map((c) => c.orgConfigKey),
] as const;

const defaultConfigs: ChannelsStoreConfig = {
  ...Object.fromEntries(keys.map((key) => [key, false])),
};
const DESKTOP_CHANNEL_TO_FLAG_MAP: Record<
  DesktopChannel,
  keyof Pick<OrgConfigs, "hide_whatsapp_channel" | "hide_telegram_channel" | "allow_imessage">
> = {
  whatsapp: "hide_whatsapp_channel",
  telegram: "hide_telegram_channel",
  imessage: "allow_imessage",
} as const;

const t = i18n.t.bind(i18n);

@injectable()
export class DestinationsStore {
  private getTestConnectionErrorToastText(error?: string) {
    if (!error) return t("destinations_page.test_connection_failed_toast");
    if (error.includes(AMAZON_TRANSCRIBE_S3_VALIDATION_ERROR)) {
      return t("destinations_page.test_connection_s3_validation_failed_toast");
    }
    if (error.includes(AMAZON_TRANSCRIBE_API_VALIDATION_ERROR)) {
      return t("destinations_page.test_connection_transcribe_validation_failed_toast");
    }
    return t("destinations_page.test_connection_failed_toast");
  }
  public initialized = false;
  public configs = defaultConfigs;
  private desktopChannels = {
    whatsapp: false,
    telegram: false,
    imessage: false,
  };
  private listFetchGeneration = 0;
  private initForceRefresh = 0;
  private lastInitForceRefresh = 0;
  testConnectionPromise: IPromiseBasedObservable<TestConnectionResponse | void> | undefined;
  private initPromise: IPromiseBasedObservable<void> | null = null;

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(WhatsAppBusinessApiSettingsStore) private readonly whatsAppBusinessApiSettingsStore: WhatsAppBusinessApiSettingsStore,
    @inject(TwilioSettingsStore) private readonly twilioSettingsStore: TwilioSettingsStore,
    @inject(SlackSettingsStore) private readonly slackSettingsStore: SlackSettingsStore,
    @inject(TeamsSettingsStore) private readonly teamsSettingsStore: TeamsSettingsStore,
    @inject(ArchiveTeamsSettingsStore) private readonly archiveTeamsSettingsStore: ArchiveTeamsSettingsStore,
    @inject(ArchiveDiscordSettingsStore) private readonly archiveDiscordSettingsStore: ArchiveDiscordSettingsStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable<DestinationsStore, "listFetchGeneration" | "desktopChannels" | "initForceRefresh" | "lastInitForceRefresh" | "configs">(this, {
      desktopChannels: observable,
      destinations: computed,
      destinationsShallow: computed,
      init: computed,
      listFetchGeneration: observable,
      initForceRefresh: observable,
      lastInitForceRefresh: observable,
      initialized: observable,
      configs: observable,
      setForceRefresh: action,
      testConnectionPromise: observable,
      testConnection: action,
      orgConfigs: computed,
      listOfAuditNamespaces: computed,
      supportedIncidentTypes: computed,
    });

    reaction(
      () => this.orgStore.orgId,
      () => {
        runInAction(() => {
          this.initForceRefresh++;
        });
      },
      { fireImmediately: true }
    );
  }

  get listOfAuditNamespaces() {
    const configs = this.orgConfigs.state === "fulfilled" ? this.orgConfigs.value.configs : undefined;
    return AuditLogNamespace.anyOf.filter((namespace) => configs?.[NAMESPACE_ORG_PERMISSIONS[namespace.const]]);
  }

  get supportedIncidentTypes(): IncidentTypeValue[] {
    const configs = this.orgConfigs.state === "fulfilled" ? this.orgConfigs.value.configs : undefined;
    const flags = {
      dlp: Boolean(configs?.allow_dlp),
      surveillance: Boolean(configs?.enable_surveillance),
    };
    return Object.entries(flags)
      .filter(([_, value]) => value)
      .map(([key]) => key as IncidentTypeValue);
  }

  get orgConfigs() {
    return fromPromise(
      this.orgConfigsStore
        .getObservableConfigs([
          "access_audit_log",
          "allow_audio_transcribes",
          "enable_widget_ai_assistant",
          "show_chat_summarize_action",
          ...uniq(Object.values(NAMESPACE_ORG_PERMISSIONS)),
        ])
        .then(
          (configs) =>
            ({
              success: true,
              configs: configs ?? {},
            }) as const
        )
    );
  }

  get channelsForDestinations(): ChannelsOverviewItemForDestinations[] {
    return [
      {
        name: `Slack`,
        icon: FaSlack,
        navigateTo: "/chat/destinations/integration/slack",
        permission: Boolean(
          hasAnyPermission(this.userStore.user, [UserPermissions["slack.view_settings"], UserPermissions["slack.update_settings"]])
        ),
        showRevokeButton: false,
        connected: this.isPlatformChannelEnabled(PLATFORM_TYPES.SLACK),
        category: "platforms",
        integrationType: "platforms",
        isActive: () => true,
      },
      {
        name: `Teams`,
        icon: BsMicrosoftTeams,
        navigateTo: "/chat/destinations/integration/teams",
        permission: hasAnyPermission(this.userStore.user, [UserPermissions["teams.view_settings"], UserPermissions["teams.update_settings"]]),
        showRevokeButton: false,
        connected: this.isPlatformChannelEnabled(PLATFORM_TYPES.TUVIS_TEAMS_APP),
        category: "platforms",
        integrationType: "platforms",
        isActive: () => true,
      },
      {
        name: `Teams`,
        icon: BsMicrosoftTeams,
        navigateTo: "/chat/destinations/integration/archive/teams",
        permission: Boolean(
          this.userStore.user?.allow_teams_archive &&
            hasAnyPermission(this.userStore.user, [UserPermissions["archive.view_settings"], UserPermissions["archive.update_settings"]])
        ),
        showRevokeButton: false,
        connected: this.archiveTeamsSettingsStore.isInitialized && this.archiveTeamsSettingsStore.connected,
        category: "archive",
        integrationType: "archive",
        isActive: () => true,
      },
      {
        name: `Discord`,
        icon: FaDiscord,
        navigateTo: "/chat/destinations/integration/archive/discord",
        permission: Boolean(
          this.userStore.user?.allow_archive &&
            hasAnyPermission(this.userStore.user, [UserPermissions["archive.view_settings"], UserPermissions["archive.update_settings"]])
        ),
        showRevokeButton: false,
        connected: this.archiveDiscordSettingsStore.isInitialized && this.archiveDiscordSettingsStore.connected,
        category: "archive",
        integrationType: "archive",
        isActive: () => true,
      },
      {
        name: `WhatsApp Business API`,
        img: `https://assets.whatslly.com/product/img/whatsapp-business-api-logo.png`,
        navigateTo: "/chat/destinations/integration/waba",
        permission: Boolean(hasAnyPermission(this.userStore.user, [UserPermissions["waba.view_settings"], UserPermissions["waba.update_settings"]])),
        showRevokeButton: false,
        connected: Boolean(this.whatsAppBusinessApiSettingsStore.isInitialized && this.whatsAppBusinessApiSettingsStore.connected),
        category: "channels",
        integrationType: "channels",
        isActive: () => true,
      },
      {
        name: `Tuvis Call Recording`,
        img: "tuvis",
        navigateTo: "/chat/destinations/integration/tuvis-call-recording",
        permission: Boolean(
          hasAnyPermission(this.userStore.user, [UserPermissions["twilio.view_settings"], UserPermissions["twilio.update_settings"]])
        ),
        showRevokeButton: false,
        connected: Boolean(this.twilioSettingsStore.isInitialized && this.twilioSettingsStore.connected),
        imgType: "svg",
        category: "channels",
        integrationType: "channels",
        isActive: () => true,
      },
    ];
  }
  setForceRefresh() {
    this.listFetchGeneration++;
  }

  get init() {
    if (this.initForceRefresh !== this.lastInitForceRefresh) {
      this.initPromise = null;
      this.lastInitForceRefresh = this.initForceRefresh;
    }

    const { state, value } = this.orgStore.orgId;
    if ((state === "fulfilled" && value === "") || state === "pending") {
      return fromPromise(Promise.resolve());
    }

    if (!this.initPromise) {
      const initializationPromise = Promise.all([
        this.getConfig(),
        this.whatsAppBusinessApiSettingsStore.loadPhones(),
        this.twilioSettingsStore.loadPhones(),
        this.slackSettingsStore.init(),
        this.teamsSettingsStore.init(),
        this.archiveTeamsSettingsStore.init(),
        this.archiveDiscordSettingsStore.init(),
      ]);

      this.initPromise = fromPromise(
        initializationPromise.then(() =>
          runInAction(() => {
            this.initialized = true;
          })
        )
      );
    }

    return this.initPromise;
  }

  get destinationsShallow() {
    void this.listFetchGeneration;
    void this.init;

    return fromPromise<(ListDestinationsShallowResponse & { success: true })["destinations"]>(
      this.orgStore.orgId
        .then((orgId) => getJSON({ urlSuffix: `/message/destinations/${orgId}/shallow`, etoken: this.userStore.etoken ?? "" }))
        .then((r) => parseOrReturnError<ListDestinationsShallowResponse>(r, this.logger))
        .then(async (res) => {
          if (res.success) {
            return res.destinations;
          }
          throw new Error(t("destinations_page.failed_to_get_integrations"));
        })
    );
  }

  get destinations() {
    void this.listFetchGeneration;
    void this.init;

    const { state, value } = this.orgStore.orgId;
    if ((state === "fulfilled" && value === "") || state === "pending") {
      return fromPromise(Promise.resolve([]));
    }

    return fromPromise<(ListDestinationsResponse & { success: true })["destinations"]>(
      this.orgStore.orgId
        .then((orgId) => getJSON({ urlSuffix: `/message/destinations/${orgId}`, etoken: this.userStore.etoken ?? "" }))
        .then((r) => parseOrReturnError<ListDestinationsResponse>(r, this.logger))
        .then(async (res) => {
          if (res.success) {
            return res.destinations;
          }
          throw new Error(t("destinations_page.failed_to_get_integrations"));
        })
    );
  }

  async createDestinations(data: Omit<MessageDestination, "orgId" | "scope">) {
    const orgId = await this.orgStore.orgId;
    const body: Omit<MessageDestination, "_id"> = {
      ...data,
      orgId,
      scope: "org",
    };
    const res = await postJSON({
      urlSuffix: `/message/destinations/${orgId}`,
      body: JSON.stringify(body),
      etoken: this.userStore.etoken ?? "",
    }).then((r) => parseOrReturnError<ListDestinationsResponse>(r, this.logger));
    this.setForceRefresh();
    return res;
  }

  async createSecurityDestination(type: SecurityIntegrations["type"]) {
    return this.createDestinations({
      integrationType: DESTINATION_INTEGRATION_TYPES.SECURITY,
      type,
      settings: {},
      extraSettings: {},
    });
  }

  async createFileScanDestination(type: FileScanningIntegrations["type"]) {
    return this.createDestinations({
      integrationType: DESTINATION_INTEGRATION_TYPES.FILE_SCANNING,
      type,
      settings: {},
      extraSettings: {},
    });
  }

  async toggleSimpleDestination(
    t: TFunction,
    destination:
      | { name: string; type: SecurityIntegrations["type"]; integrationType: typeof DESTINATION_INTEGRATION_TYPES.SECURITY }
      | { name: string; type: FileScanningIntegrations["type"]; integrationType: typeof DESTINATION_INTEGRATION_TYPES.FILE_SCANNING },
    destinationId?: string
  ) {
    const isEnabled = Boolean(destinationId);
    const confirm = await modalService.openModal(ConfirmModal, {
      body: t(isEnabled ? "destinations_page.disable_destination_confirmation" : "destinations_page.enable_destination_confirmation", {
        destinationName: destination.name,
      }),
      okText: t("channels.confirm_button"),
      cancelText: t("destinations_page.cancel_button_text"),
    }).promise;
    if (!confirm?.result) return;

    modalService.openModal(Toast, {
      text: t(isEnabled ? "destinations_page.revoke_destination_in_progress" : "destinations_page.create_destination_in_progress"),
    });
    const res = isEnabled
      ? await this.deleteDestinations(destinationId!)
      : destination.integrationType === DESTINATION_INTEGRATION_TYPES.FILE_SCANNING
        ? await this.createFileScanDestination(destination.type)
        : await this.createSecurityDestination(destination.type);
    modalService.openModal(Toast, {
      text: res?.success
        ? t(isEnabled ? "destinations_page.destination_revoked_successfully" : "destinations_page.destination_created_successfully")
        : t(isEnabled ? "destinations_page.failed_to_revoke_destination" : "destinations_page.failed_to_create_destination"),
      type: res?.success ? "regular" : "error",
    });
  }

  async editOtherSecurityDestination(t: TFunction, destination?: MessageDestination) {
    const currentName = isOtherSecurityDestination(destination) ? destination.settings.securityStack ?? "" : "";
    const modalRes = await modalService.openModal(SecurityOtherModal, { initialName: currentName }).promise;
    if (!modalRes) return;
    const securityStack = modalRes.name.trim();

    // Empty name means "no Other stack" — remove the destination entirely.
    if (!securityStack) {
      if (!destination?._id) return;
      modalService.openModal(Toast, { text: t("destinations_page.revoke_destination_in_progress") });
      const deleteRes = await this.deleteDestinations(destination._id);
      modalService.openModal(Toast, {
        text: deleteRes?.success ? t("destinations_page.destination_revoked_successfully") : t("destinations_page.failed_to_revoke_destination"),
        type: deleteRes?.success ? "regular" : "error",
      });
      return;
    }

    modalService.openModal(Toast, {
      text: t(destination?._id ? "destinations_page.update_destination_in_progress" : "destinations_page.create_destination_in_progress"),
    });
    let res;
    if (isOtherSecurityDestination(destination) && destination._id) {
      const updated = cloneDeep(destination);
      updated.settings = { securityStack };
      res = await this.updateDestinations(updated);
    } else {
      res = await this.createDestinations({
        integrationType: DESTINATION_INTEGRATION_TYPES.SECURITY,
        type: "other",
        settings: { securityStack },
        extraSettings: {},
      });
    }
    modalService.openModal(Toast, {
      text: res?.success
        ? t(destination?._id ? "destinations_page.destination_updated_successfully" : "destinations_page.destination_created_successfully")
        : t(destination?._id ? "destinations_page.failed_to_update_destination" : "destinations_page.failed_to_create_destination"),
      type: res?.success ? "regular" : "error",
    });
  }

  async triggerDesktopChannel(channel: DesktopChannel) {
    const channelName = channel === "whatsapp" ? "WhatsApp" : channel === "telegram" ? "Telegram" : "iMessage";
    const res = await modalService.openModal(ConfirmModal, {
      body: this.desktopChannels[channel]
        ? t("channels.confirm_disable_modal_text", { channel: channelName })
        : t("channels.confirm_enable_modal_text", { channel: channelName }),
      okText: t("channels.confirm_button"),
    }).promise;
    if (!res?.result) {
      return;
    }
    this.setDesktopChannel(channel, !this.desktopChannels[channel]);
  }

  getDesktopChannel(channel: DesktopChannel) {
    return this.desktopChannels[channel];
  }

  isChannelEnabled(channel: CommonChannel) {
    if (channel === "waba") {
      return this.whatsAppBusinessApiSettingsStore.isInitialized;
    } else if (channel === "twilio") {
      return this.twilioSettingsStore.isInitialized;
    }
    return false;
  }

  isPlatformChannelEnabled(channel: PlatformType) {
    if (channel === PLATFORM_TYPES.SLACK) {
      return this.slackSettingsStore.isInitialized && this.slackSettingsStore.connected;
    }
    if (channel === PLATFORM_TYPES.TUVIS_TEAMS_APP) {
      return this.teamsSettingsStore.isInitialized && this.teamsSettingsStore.connected;
    }
    return false;
  }

  private async getConfig() {
    const res = await this.orgConfigsStore.getObservableConfigs(keys);
    runInAction(() => {
      this.desktopChannels.whatsapp = !res?.hide_whatsapp_channel;
      this.desktopChannels.telegram = !res?.hide_telegram_channel;
      this.desktopChannels.imessage = Boolean(res?.allow_imessage);
      this.configs = res ?? defaultConfigs;
    });
  }

  async setDesktopChannel(channel: DesktopChannel, value: boolean) {
    const flag = DESKTOP_CHANNEL_TO_FLAG_MAP[channel];
    const flagValue = channel === "whatsapp" || channel === "telegram" ? !value : value;
    const res = await this.orgConfigsStore.saveConfig(flag, flagValue);
    if (res.success) {
      runInAction(() => {
        this.desktopChannels[channel] = value;
      });
    }
  }
  async updateDestinations(data: MessageDestination) {
    const orgId = await this.orgStore.orgId;
    const res = await putJSON({
      urlSuffix: `/message/destinations/${orgId}/${data._id}`,
      body: JSON.stringify(data),
      etoken: this.userStore.etoken ?? "",
    }).then((r) => parseOrReturnError<ListDestinationsResponse>(r, this.logger));
    this.setForceRefresh();
    return res;
  }

  async updateDestinationDetails(destinationId: string, details: { integrationName?: string; groups?: string[]; isViewOnly?: boolean }) {
    const orgId = await this.orgStore.orgId;
    const res = await putJSON({
      urlSuffix: `/message/destinations/${orgId}/${destinationId}/details`,
      body: JSON.stringify(details),
      etoken: this.userStore.etoken ?? "",
    }).then((r) => parseOrReturnError<ListDestinationsResponse>(r, this.logger));
    this.setForceRefresh();
    return res;
  }

  async deleteDestinations(id: string) {
    const orgId = await this.orgStore.orgId;
    const res = await deleteJSON({
      urlSuffix: `/message/destinations/${orgId}/${id}`,
      etoken: this.userStore.etoken ?? "",
      body: "{}",
    }).then((r) => parseOrReturnError<ListDestinationsResponse>(r, this.logger));
    this.setForceRefresh();
    return res;
  }

  testConnection(t: TFunction, data: Destination) {
    this.testConnectionPromise = fromPromise(
      Promise.resolve(this.orgStore.orgId)
        .then((orgId) =>
          postJSON({
            urlSuffix: `/message/destinations/${orgId}/test-connection`,
            etoken: this.userStore.etoken ?? "",
            body: JSON.stringify(data ?? {}),
          })
        )
        .then((r) => parseOrReturnError<TestConnectionResponse>(r, this.logger))
        .then((r) => {
          if (r.success) {
            return r;
          }
          modalService.openModal(Toast, { text: this.getTestConnectionErrorToastText(r.error), type: "error" });
        })
        .catch(() => {
          modalService.openModal(Toast, { text: t("destinations_page.test_connection_failed_toast"), type: "error" });
        })
    );
  }
}
